import React, { useState } from 'react';
import './BillingDetails.css';
import { useAuth } from '../../../Security/AuthProvider';
import { useNotification } from "../../../Utils/Commons/Notification/NotificationContext";

const BillingDetails = ({ data, onRdvStatusUpdate }) => {
  const { getCredentials } = useAuth();
  const { notifyError, notifySuccess } = useNotification();
  const [loading, setLoading] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (timeSlot) => {
    const minutes = (timeSlot * 15) % 60;
    const hours = Math.floor((timeSlot * 15) / 60) + 8;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f39c12';
      case 'billed': return '#3498db';
      case 'paid': return '#27ae60';
      default: return '#95a5a6';
    }
  };

  const getCreationTypeIcon = (type) => {
    switch (type) {
      case 'automatic': return '🤖';
      case 'semi_automatic': return '⚙️';
      case 'manual': return '✋';
      default: return '❓';
    }
  };

  const handleStatusUpdate = async (rdvId, newStatus) => {
    if (loading === rdvId) return;

    try {
      setLoading(rdvId);
      const credentials = getCredentials();
      const endpoint = newStatus === 'billed' ? '/billing/mark-billed' : '/billing/mark-paid';
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_name: credentials.company_name,
          company_password: credentials.company_password,
          rdv_id: rdvId,
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        onRdvStatusUpdate();
        notifySuccess('Statut mis à jour avec succès');
      } else {
        notifyError('Erreur lors de la mise à jour du statut');
      }
    } catch (error) {
      console.error('Erreur:', error);
      notifyError('Erreur lors de la mise à jour du statut');
    } finally {
      setLoading(null);
    }
  };

  if (!data || !data.rdv_list || data.rdv_list.length === 0) {
    return (
      <div className="billing-details">
        <div className="no-data-message">
          <h3>Aucun rendez-vous à afficher</h3>
          <p>Aucun rendez-vous n'a été enregistré pour ce mois.</p>
        </div>
      </div>
    );
  }

  let filteredRdvs = data.rdv_list;

  // Filtrage
  if (filter !== 'all') {
    filteredRdvs = filteredRdvs.filter(rdv => rdv.billing_status === filter);
  }

  // Tri
  filteredRdvs.sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b.date) - new Date(a.date);
      case 'client':
        return a.client_name.localeCompare(b.client_name);
      case 'agent':
        return a.agent_name.localeCompare(b.agent_name);
      case 'type':
        return a.creation_type.localeCompare(b.creation_type);
      default:
        return 0;
    }
  });

  return (
    <div className="billing-details">
      <div className="details-header">
        <h2>Détails des Rendez-vous</h2>
        <div className="details-controls">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="filter-select">
            <option value="all">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="billed">Facturé</option>
            <option value="paid">Payé</option>
          </select>
          
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
            <option value="date">Trier par date</option>
            <option value="client">Trier par client</option>
            <option value="agent">Trier par agent</option>
            <option value="type">Trier par type</option>
          </select>
        </div>
      </div>

      <div className="rdv-count">
        {filteredRdvs.length} rendez-vous trouvé(s)
      </div>

      <div className="rdv-table">
        <div className="table-header">
          <div>Date</div>
          <div>Heure</div>
          <div>Client</div>
          <div>Agent</div>
          <div>Opération</div>
          <div>Type</div>
          <div>Durée</div>
          <div>Statut</div>
          <div>Actions</div>
        </div>

        {filteredRdvs.map((rdv) => (
          <div key={rdv.id} className="table-row">
            <div className="cell date-cell">
              {formatDate(rdv.date)}
            </div>
            
            <div className="cell time-cell">
              {formatTime(rdv.beginning)} - {formatTime(rdv.ending)}
            </div>
            
            <div className="cell client-cell">
              <div className="client-info">
                <div className="client-name">{rdv.client_name}</div>
                {rdv.client_email && (
                  <div className="client-email">{rdv.client_email}</div>
                )}
              </div>
            </div>
            
            <div className="cell agent-cell">
              {rdv.agent_name}
            </div>
            
            <div className="cell operation-cell">
              {rdv.operation_name || 'N/A'}
            </div>
            
            <div className="cell type-cell">
              <span className={`type-badge ${rdv.creation_type}`}>
                {getCreationTypeIcon(rdv.creation_type)} {rdv.creation_type}
              </span>
            </div>
            
            <div className="cell duration-cell">
              {rdv.duration_minutes} min
            </div>
            
            <div className="cell status-cell">
              <span 
                className="status-badge" 
                style={{ backgroundColor: getStatusColor(rdv.billing_status) }}
              >
                {rdv.billing_status === 'pending' ? 'En attente' :
                 rdv.billing_status === 'billed' ? 'Facturé' :
                 rdv.billing_status === 'paid' ? 'Payé' : rdv.billing_status}
              </span>
            </div>
            
            <div className="cell actions-cell">
              {rdv.billing_status === 'pending' && (
                <button 
                  onClick={() => handleStatusUpdate(rdv.id, 'billed')}
                  disabled={loading === rdv.id}
                  className="action-button billed"
                >
                  {loading === rdv.id ? '...' : '📄 Marquer facturé'}
                </button>
              )}
              
              {rdv.billing_status === 'billed' && (
                <button 
                  onClick={() => handleStatusUpdate(rdv.id, 'paid')}
                  disabled={loading === rdv.id}
                  className="action-button paid"
                >
                  {loading === rdv.id ? '...' : '💰 Marquer payé'}
                </button>
              )}
              
              {rdv.billing_status === 'paid' && (
                <span className="paid-indicator">✅ Payé</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BillingDetails; 